import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  User,
  Target,
  Crown,
  Rocket,
  Zap,
  Bot,
  Sparkles,
  Brain,
  MapPin,
  Briefcase,
  Heart,
  Users,
  GraduationCap,
  BookOpen,
  Wrench,
  Map as MapIcon,
  Download,
  HelpCircle,
  FileText,
  CreditCard,
  Phone,
  ArrowRight,
  ExternalLink,
  type LucideIcon,
} from 'lucide-react';
import Button from '../ui/button';
import AuthModal from '../auth/auth-modal';
import HubSpotModal from '../ui/hubspot-modal';
import { supabase, safeSupabaseQuery } from '../../lib/supabase';
import { useAuth } from '../../AuthProvider';

// — Types nav ————————————————————————————————————————————————————————

type NavLink = {
  label: string;
  path: string;
  description?: string;
  Icon?: LucideIcon;
  external?: boolean;
  featured?: boolean;
};

type NavColumn = {
  title: string;
  Icon?: LucideIcon;
  items: NavLink[];
};

type NavSection = {
  label: string;
  path: string;
  // mega-menu : si présent, on rend un panel large multi-colonnes
  subtitle?: string;
  columns?: NavColumn[];
  cta?: { label: string; path: string };
  // dropdown simple : utilisé pour Infos (peu d'items, pas besoin de mega)
  children?: NavLink[];
};

// — Données nav ——————————————————————————————————————————————————————

const navItems: NavSection[] = [
  {
    label: 'Entreprise',
    path: '/entreprise/recrutement',
    subtitle:
      'Tout ce dont une PME a besoin pour structurer son recrutement.',
    columns: [
      {
        title: 'Nos services',
        Icon: Briefcase,
        items: [
          {
            label: "Recrutement à l'unité",
            path: '/entreprise/recrutement',
            description: 'Sourcing chasse + IA pour 1 poste critique',
            Icon: Target,
          },
          {
            label: 'Chasse de dirigeants',
            path: '/entreprise/recrutement-dirigeants',
            description: 'Executive search confidentiel · CEO, DG, DAF, DRH',
            Icon: Crown,
          },
          {
            label: 'Campagnes & vivier',
            path: '/entreprise/campagnes',
            description: 'Vivier continu, candidats acculturés',
            Icon: Rocket,
          },
          {
            label: 'Externalisation RH',
            path: '/entreprise/externalisation',
            description: 'DRH à la demande, sur projet',
            Icon: Zap,
          },
        ],
      },
      {
        title: 'Nos signatures',
        Icon: Sparkles,
        items: [
          {
            label: 'Plateforme Parcours Découverte',
            path: '/decouvrir',
            description: 'Vu par le candidat avant signature',
            Icon: Sparkles,
            featured: true,
          },
          {
            label: 'IA RH sur-mesure',
            path: '/entreprise/ia',
            description: 'Chatbots, prédictif, génératif',
            Icon: Bot,
          },
          {
            label: 'Coach TalentHrAi',
            path: '/entreprise/talent-rh-ai',
            description: 'Assistant IA pour votre fonction RH',
            Icon: Brain,
          },
        ],
      },
      {
        title: 'Où nous recrutons',
        Icon: MapPin,
        items: [
          {
            label: 'Marseille',
            path: '/cabinet-recrutement-marseille',
            description: 'Notre cabinet historique',
            Icon: MapPin,
          },
          {
            label: 'Aix-en-Provence',
            path: '/cabinet-recrutement-aix-en-provence',
            description: "Pays d'Aix · Tech",
            Icon: MapPin,
          },
          {
            label: 'PACA',
            path: '/cabinet-recrutement-paca',
            description: 'Couverture régionale',
            Icon: MapPin,
          },
          {
            label: 'Poitiers',
            path: '/cabinet-recrutement-poitiers',
            description: 'Partenariat Thomaz RH',
            Icon: MapPin,
          },
        ],
      },
    ],
    cta: {
      label: 'Pas sûr de ce dont vous avez besoin ? Réserver 15 min →',
      path: '/reservation',
    },
  },
  {
    label: 'IA PACA',
    path: '/ia-paca',
    subtitle:
      "Cabinet de recrutement IA en Provence-Alpes-Côte d'Azur. 13 métiers, 4 villes, outils gratuits.",
    columns: [
      {
        title: 'Villes',
        Icon: MapPin,
        items: [
          {
            label: 'Hub IA PACA',
            path: '/ia-paca',
            description: 'Vue d\'ensemble du marché IA en région',
            Icon: Sparkles,
            featured: true,
          },
          {
            label: 'Marseille',
            path: '/ia-paca/marseille',
            description: 'CMA CGM × Mistral, AIM 2026',
            Icon: MapPin,
          },
          {
            label: 'Aix-en-Provence',
            path: '/ia-paca/aix-en-provence',
            description: 'Arbois, Pôle Pégase, ETI industrielles',
            Icon: MapPin,
          },
          {
            label: 'Sophia Antipolis',
            path: '/ia-paca/sophia-antipolis',
            description: '2 500 entreprises tech · Pôle Alpha',
            Icon: MapPin,
          },
          {
            label: 'Nice',
            path: '/ia-paca/nice',
            description: 'MedTech, smart city, FinTech',
            Icon: MapPin,
          },
        ],
      },
      {
        title: 'Métiers IA',
        Icon: Brain,
        items: [
          {
            label: 'AI Engineer',
            path: '/ia-paca/metiers/ai-engineer',
            description: 'RAG, agents, LLM en prod',
            Icon: Brain,
          },
          {
            label: 'ML Engineer',
            path: '/ia-paca/metiers/ml-engineer',
            description: 'PyTorch, MLflow, industrialisation',
            Icon: Brain,
          },
          {
            label: 'MLOps Engineer',
            path: '/ia-paca/metiers/mlops-engineer',
            description: 'Kubernetes, GPU, plateforme ML',
            Icon: Brain,
          },
          {
            label: 'Data Scientist',
            path: '/ia-paca/metiers/data-scientist',
            description: 'Stats, ML, A/B testing',
            Icon: Brain,
          },
          {
            label: 'Agentic AI Engineer',
            path: '/ia-paca/metiers/agentic-ai-engineer',
            description: 'LangGraph, multi-agents · émergent',
            Icon: Bot,
            featured: true,
          },
          {
            label: 'Voir les 13 métiers →',
            path: '/ia-paca',
            description: 'AI PM, Architecte, Data Eng IA, Consultant…',
            Icon: ArrowRight,
          },
        ],
      },
      {
        title: 'Outils & analyses',
        Icon: Wrench,
        items: [
          {
            label: 'Carte interactive entreprises',
            path: '/ia-paca/carte-entreprises',
            description: '35 acteurs IA cartographiés en PACA',
            Icon: MapIcon,
            featured: true,
          },
          {
            label: 'Baromètre salaires IA 2026',
            path: '/ia-paca/barometre-salaires-ia-paca-2026',
            description: '13 métiers · 4 villes · 4 séniorités',
            Icon: Download,
          },
          {
            label: 'Quiz AI Readiness Score',
            path: '/ia-paca/quiz-ai-readiness',
            description: 'Maturité IA en 15 questions',
            Icon: HelpCircle,
          },
          {
            label: 'Enquête : 80% des AI Engineers PACA…',
            path: '/ia-paca/articles/ai-engineers-rag-production',
            description: '…ne savent pas faire un RAG en prod',
            Icon: BookOpen,
          },
        ],
      },
    ],
    cta: {
      label: 'Briefer un recrutement IA → Réserver 30 min',
      path: '/reservation',
    },
  },
  {
    label: 'Candidat',
    path: '/offres',
    subtitle:
      'Le recrutement traité avec respect, du clic « postuler » à votre prise de poste.',
    columns: [
      {
        title: 'Trouver un poste',
        Icon: Briefcase,
        items: [
          {
            label: "Offres d'emploi",
            path: '/offres',
            description: 'Toutes nos opportunités du moment',
            Icon: Briefcase,
          },
          {
            label: 'Pourquoi recruteurs.io',
            path: '/candidat/pourquoi',
            description: '4 engagements opposables côté candidat',
            Icon: Heart,
          },
        ],
      },
      {
        title: 'Aller plus loin',
        Icon: Sparkles,
        items: [
          {
            label: "Découvrir l'entreprise avant de signer",
            path: '/candidat/decouvrir',
            description: 'Parcours Découverte côté candidat',
            Icon: Sparkles,
            featured: true,
          },
          {
            label: 'Coach Talent AI',
            path: '/candidat/talentai',
            description: 'Préparation entretiens, simulations',
            Icon: GraduationCap,
          },
        ],
      },
    ],
    cta: {
      label: "Voir toutes les offres en cours →",
      path: '/offres',
    },
  },
  {
    label: 'Ressources',
    path: '/ressources/articles',
    subtitle:
      "L'IA RH n'est pas un secret. On partage nos contenus et nos outils.",
    columns: [
      {
        title: 'Contenus',
        Icon: BookOpen,
        items: [
          {
            label: 'Articles RH',
            path: '/ressources/articles',
            description: 'Insights, méthodes, retours terrain',
            Icon: BookOpen,
          },
          {
            label: 'Ressources téléchargeables',
            path: '/ressources/ressources',
            description: 'Templates, guides, checklists',
            Icon: Download,
          },
        ],
      },
      {
        title: 'Outils gratuits',
        Icon: Wrench,
        items: [
          {
            label: 'Mapping RH',
            path: '/ressources/mapping-rh',
            description: 'Test compatibilité candidat-équipe (GPT-4)',
            Icon: MapIcon,
            featured: true,
          },
          {
            label: 'Suivi de candidatures',
            path: '/ressources/outils/suivi-candidature',
            description: 'Tracker gratuit pour candidats (kanban + stats)',
            Icon: Briefcase,
          },
          {
            label: "Catalogue d'outils",
            path: '/ressources/outils',
            description: 'Notre veille IA + RH',
            Icon: Wrench,
          },
        ],
      },
    ],
  },
  {
    label: 'Infos',
    path: '/infos/questions',
    // Format simple pour Infos (4 items, pas besoin de mega-menu)
    children: [
      { label: 'Questions fréquentes', path: '/infos/questions', Icon: HelpCircle },
      { label: "Charte d'engagement", path: '/infos/charte', Icon: FileText },
      { label: 'Nos tarifs', path: '/infos/tarifs', Icon: CreditCard },
      { label: 'Contact', path: '/reservation', Icon: Phone },
    ],
  },
];

const adminItems: NavSection = {
  label: 'Admin',
  path: '/admin',
  // Format simple (dropdown classique) pour la navigation admin
  children: [
    { label: 'Gestion des utilisateurs', path: '/admin/users' },
    { label: "Offres d'emploi", path: '/admin/offres' },
    { label: 'Parcours', path: '/admin/parcours' },
    { label: '✨ Générateur LinkedIn', path: '/admin/linkedin-generator' },
    { label: '🛡️ Surveillance sécurité', path: '/admin/security' },
    { label: 'Timeline Recrutement', path: '/admin/timeline-recrutement' },
    { label: 'Timeline Campagnes', path: '/admin/timeline-campagnes' },
    { label: 'Forecasts', path: '/admin/forecasts' },
    // 'Dashboard Client' retiré — route /client/dashboard jamais implémentée,
    // générait un Soft 404 GSC. Voir netlify.toml (410 Gone /client/*).
  ],
};

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);

  // SSO entry point: open the auth modal when arriving with ?auth=open
  // (typically a redirect from https://app.recruteurs.io when unauthed).
  // The `next` param controls where to bounce after a successful login.
  const ssoNext = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('next');
    if (!raw) return null;
    try {
      const url = new URL(raw, window.location.origin);
      const host = url.hostname;
      // Restrict to the recruteurs.io family + localhost (dev).
      const allowed =
        host === window.location.hostname ||
        host === 'recruteurs.io' ||
        host.endsWith('.recruteurs.io') ||
        host === 'localhost' ||
        host === '127.0.0.1';
      return allowed ? url.toString() : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth') === 'open') {
      setIsAuthModalOpen(true);
    }
  }, []);
  const [isAdmin, setIsAdmin] = useState(false);
  const [logo, setLogo] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const { data, error, isCorsError } = await safeSupabaseQuery(
          () => supabase
            .from('site_settings')
            .select('value')
            .eq('key', 'logo')
            .maybeSingle()
        );

        if (isCorsError) {
          console.warn('CORS error fetching logo, using fallback');
          setLogoError(true);
          return;
        }

        if (!error && data?.value) {
          setLogo(data.value);
        } else {
          setLogoError(true);
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
        setLogoError(true);
      }
    };
    fetchLogo();
  }, []);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) return setIsAdmin(false);
      
      const { data, error, isCorsError } = await safeSupabaseQuery(
        () => supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .maybeSingle()
      );

      if (isCorsError) {
        console.warn('CORS error checking admin status, defaulting to non-admin');
        setIsAdmin(false);
        return;
      }

      if (error || !data) {
        console.error('Error fetching user role:', error);
        return setIsAdmin(false);
      }

      setIsAdmin(data.role === 'admin');
    };

    checkAdmin();
  }, [user]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Lock body scroll quand le menu mobile est ouvert (évite que la page scroll
  // sous le menu quand on tente de scroller dans le menu lui-même)
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = '/?logout=' + Date.now();
    } catch (error) {
      window.location.href = '/?force_logout=' + Date.now();
    }
  };

  const handleNavigation = (path: string, external?: boolean) => {
    if (external) window.open(path, '_blank');
    else window.location.href = path;
  };

  const allNavItems = isAdmin ? [...navItems, adminItems] : navItems;

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur border-b border-neutral-200 py-2' : 'bg-transparent py-4'}`}>
      <div className="container-custom flex items-center justify-between">
        <Link to="/" className="flex items-center">
          {!logoError && logo ? (
            <img src={logo} alt="recruteurs.io" className="h-12 object-contain" />
          ) : (
            <span className="text-xl font-title font-bold text-left gradient-text">recruteurs.io</span>
          )}
        </Link>

        <nav className="hidden md:flex items-center space-x-8">
          {allNavItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => setActiveDropdown(item.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link
                to={item.path}
                className={`relative group font-medium transition-colors text-neutral-800 hover:text-[#d03484] ${location.pathname.startsWith(item.path) ? 'text-[#d03484]' : ''}`}
              >
                {item.label}
                <span className="absolute left-0 bottom-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-[#d03484] to-[#d47254] transition-all duration-300" />
              </Link>

              <AnimatePresence>
                {activeDropdown === item.label && (
                  <>
                    {/* MEGA-MENU : si l'item a des colonnes, on rend un panel large */}
                    {item.columns && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, y: 8, x: '-50%' }}
                        transition={{ duration: 0.2 }}
                        className={`fixed left-1/2 w-[min(92vw,1080px)] bg-white rounded-[1.5rem] shadow-2xl ring-1 ring-neutral-200 overflow-hidden z-50 ${
                          scrolled ? 'top-[4.5rem]' : 'top-[5.5rem]'
                        }`}
                      >
                        {/* Pont invisible pour ne pas perdre le hover entre le label et la dropdown */}
                        <div aria-hidden className="absolute -top-10 left-0 right-0 h-10" />

                        {/* Header du mega-menu (subtitle) */}
                        {item.subtitle && (
                          <div className="px-8 py-4 border-b border-neutral-100 bg-[#fbfaf8]">
                            <p className="text-sm text-neutral-600 leading-relaxed">
                              <span className="font-semibold text-[#30475e] uppercase tracking-widest text-xs mr-2">
                                {item.label}
                              </span>
                              {item.subtitle}
                            </p>
                          </div>
                        )}

                        {/* Colonnes */}
                        <div
                          className={`grid gap-6 p-8 ${
                            item.columns.length === 3
                              ? 'grid-cols-3'
                              : item.columns.length === 2
                              ? 'grid-cols-2'
                              : 'grid-cols-1'
                          }`}
                        >
                          {item.columns.map((col) => {
                            const ColIcon = col.Icon;
                            return (
                              <div key={col.title}>
                                {/* Header de colonne */}
                                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-neutral-100">
                                  {ColIcon && (
                                    <ColIcon className="w-4 h-4 text-[#d03484]" />
                                  )}
                                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-widest">
                                    {col.title}
                                  </p>
                                </div>

                                {/* Items de la colonne */}
                                <div className="space-y-1">
                                  {col.items.map((sub) => {
                                    const SubIcon = sub.Icon;
                                    const isActive = location.pathname === sub.path;
                                    return (
                                      <div
                                        key={sub.label}
                                        onClick={() => handleNavigation(sub.path, sub.external)}
                                        className={`group/item flex items-start gap-3 rounded-xl px-3 py-2.5 cursor-pointer transition-all duration-200 ${
                                          isActive
                                            ? 'bg-gradient-to-r from-[#fde7f2] to-[#fde2d3] ring-1 ring-[#d03484]/40'
                                            : 'hover:bg-[#fbfaf8]'
                                        }`}
                                      >
                                        {SubIcon && (
                                          <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                                            sub.featured
                                              ? 'bg-gradient-to-r from-[#d03484] to-[#d47254] text-white shadow-md shadow-[#d03484]/20'
                                              : 'bg-[#fbfaf8] text-[#d03484] ring-1 ring-neutral-200 group-hover/item:bg-white'
                                          }`}>
                                            <SubIcon className="w-4 h-4" />
                                          </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-1.5">
                                            <p className={`text-sm font-semibold transition-colors ${
                                              sub.featured
                                                ? 'text-[#d03484]'
                                                : 'text-[#30475e] group-hover/item:text-[#d03484]'
                                            }`}>
                                              {sub.label}
                                            </p>
                                            {sub.external && (
                                              <ExternalLink className="w-3 h-3 text-neutral-400" />
                                            )}
                                          </div>
                                          {sub.description && (
                                            <p className="text-xs text-neutral-500 leading-snug mt-0.5">
                                              {sub.description}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* CTA bottom */}
                        {item.cta && (
                          <div className="px-8 py-4 border-t border-neutral-100 bg-[#fbfaf8]">
                            <Link
                              to={item.cta.path}
                              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#30475e] hover:text-[#d03484] transition-colors"
                            >
                              {item.cta.label}
                              <ArrowRight className="w-4 h-4" />
                            </Link>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {/* DROPDOWN CLASSIQUE : si l'item a des children directs (Infos, Admin) */}
                    {item.children && !item.columns && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-3 w-64 bg-white rounded-2xl shadow-xl ring-1 ring-neutral-200 overflow-hidden z-50"
                      >
                        <div aria-hidden className="absolute -top-3 left-0 right-0 h-3" />
                        <div className="py-2">
                          {item.children.map((child) => {
                            const ChildIcon = child.Icon;
                            const isActive = location.pathname === child.path;
                            return (
                              <div
                                key={child.label}
                                onClick={() => handleNavigation(child.path, child.external)}
                                className={`group flex items-center gap-3 px-4 py-2.5 hover:bg-[#fbfaf8] transition-all duration-200 cursor-pointer ${
                                  isActive ? 'text-[#d03484] font-semibold' : 'text-[#30475e] hover:text-[#d03484]'
                                }`}
                              >
                                {ChildIcon && (
                                  <ChildIcon className="w-4 h-4 flex-shrink-0 text-[#d03484]" />
                                )}
                                <span className="text-sm">{child.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </>
                )}
              </AnimatePresence>
            </div>
          ))}
          <div className="flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-neutral-700">
                  <User size={18} />
                  <span className="text-sm">{user.email}</span>
                </div>
                <Button variant="outline" onClick={handleLogout} className="text-neutral-900 hover:text-[#d03484]">
                  Déconnexion
                </Button>
              </div>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate('/reservation')}>
                  📅 Planifier un appel
                </Button>
                <Button variant="outline" size="sm" onClick={() => setIsAuthModalOpen(true)}>
                  <span className="text-neutral-900">⚡</span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254] ml-1">
                    login
                  </span>
                </Button>
              </>
            )}
          </div>
        </nav>

        <button className="md:hidden text-neutral-800 focus:outline-none" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-neutral-100 overflow-y-auto overscroll-contain"
            style={{ maxHeight: 'calc(100vh - 64px)' }}
          >
            <div className="px-6 py-4 space-y-5 pb-12">
              {allNavItems.map((item) => (
                <div key={item.label}>
                  <p className="font-bold text-[#30475e] text-base mb-2">{item.label}</p>

                  {/* MOBILE — colonnes (mega-menu) → on aplatit en sous-groupes avec sous-titres */}
                  {item.columns && (
                    <div className="ml-2 space-y-4">
                      {item.columns.map((col) => (
                        <div key={col.title}>
                          <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-1.5">
                            {col.title}
                          </p>
                          <div className="ml-2 space-y-0.5">
                            {col.items.map((child) =>
                              child.external ? (
                                <a
                                  key={child.label}
                                  href={child.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center gap-2 py-1.5 text-sm transition-colors ${
                                    child.featured
                                      ? 'text-[#d03484] font-semibold'
                                      : 'text-neutral-700 hover:text-[#d03484]'
                                  }`}
                                  onClick={() => setIsOpen(false)}
                                >
                                  {child.Icon && <child.Icon className="w-4 h-4 flex-shrink-0" />}
                                  <span>{child.label}</span>
                                  <ExternalLink className="w-3 h-3 text-neutral-400" />
                                </a>
                              ) : (
                                <Link
                                  key={child.label}
                                  to={child.path}
                                  className={`flex items-center gap-2 py-1.5 text-sm transition-colors ${
                                    child.featured
                                      ? 'text-[#d03484] font-semibold'
                                      : 'text-neutral-700 hover:text-[#d03484]'
                                  }`}
                                  onClick={() => setIsOpen(false)}
                                >
                                  {child.Icon && <child.Icon className="w-4 h-4 flex-shrink-0" />}
                                  <span>{child.label}</span>
                                </Link>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* MOBILE — children classiques (Infos, Admin) */}
                  {item.children && !item.columns && (
                    <div className="ml-2 space-y-0.5">
                      {item.children.map((child) =>
                        child.external ? (
                          <a
                            key={child.label}
                            href={child.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 py-1.5 text-sm text-neutral-700 hover:text-[#d03484]"
                            onClick={() => setIsOpen(false)}
                          >
                            {child.Icon && <child.Icon className="w-4 h-4 flex-shrink-0" />}
                            <span>{child.label}</span>
                            <ExternalLink className="w-3 h-3 text-neutral-400" />
                          </a>
                        ) : (
                          <Link
                            key={child.label}
                            to={child.path}
                            className="flex items-center gap-2 py-1.5 text-sm text-neutral-700 hover:text-[#d03484]"
                            onClick={() => setIsOpen(false)}
                          >
                            {child.Icon && <child.Icon className="w-4 h-4 flex-shrink-0" />}
                            <span>{child.label}</span>
                          </Link>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-neutral-100 space-y-2">
                {user ? (
                  <>
                    <div className="text-sm text-neutral-600">{user.email}</div>
                    <Button variant="gradient" fullWidth onClick={handleLogout}>
                      Déconnexion
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" fullWidth onClick={() => { navigate('/reservation'); setIsOpen(false); }}>
                      📅 Planifier un appel
                    </Button>
                    <Button variant="outline" fullWidth onClick={() => setIsAuthModalOpen(true)} className="text-center bg-gradient-to-r from-[#d03484] to-[#d47254] text-transparent bg-clip-text">
                      login
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        next={ssoNext}
      />
      <HubSpotModal isOpen={isHubSpotModalOpen} onClose={() => setIsHubSpotModalOpen(false)} />
    </header>
  );
}